import { Box, Button, Card, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import { CalendarIcon } from 'src/v3/core/icons'

interface CertificatesDateFilterProps {
  startDate: Dayjs | null
  endDate: Dayjs | null
  today: Dayjs
  hasActiveFilter: boolean
  isInvalidRange: boolean
  onStartDateChange: (value: Dayjs | null) => void
  onEndDateChange: (value: Dayjs | null) => void
  onClear: () => void
}

export default function CertificatesDateFilter({
  startDate,
  endDate,
  today,
  hasActiveFilter,
  isInvalidRange,
  onStartDateChange,
  onEndDateChange,
  onClear,
}: CertificatesDateFilterProps) {
  // The pickers hand back a non-null but invalid Dayjs while a date is being
  // typed. Feeding that to the opposite picker's min/max bound produces
  // inconsistent validation, so only constrain against a complete, valid bound.
  let validStart = startDate?.isValid() ? startDate : null
  let validEnd = endDate?.isValid() ? endDate : null

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card sx={{ mb: 4, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <CalendarIcon fontSize="small" />
            Filter by Date Range
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            onClick={onClear}
            disabled={!hasActiveFilter}
            sx={{ minWidth: 'auto' }}
          >
            All Time
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            },
          }}
        >
          <DatePicker
            label="From"
            value={startDate}
            onChange={onStartDateChange}
            maxDate={validEnd ?? today}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: { backgroundColor: 'background.paper' },
              },
              field: { clearable: true },
            }}
          />
          <DatePicker
            label="To"
            value={endDate}
            onChange={onEndDateChange}
            minDate={validStart ?? undefined}
            maxDate={today}
            slotProps={{
              textField: {
                fullWidth: true,
                error: isInvalidRange,
                helperText: isInvalidRange
                  ? 'The "To" date must be on or after the "From" date.'
                  : undefined,
                sx: { backgroundColor: 'background.paper' },
              },
              field: { clearable: true },
            }}
          />
        </Box>
      </Card>
    </LocalizationProvider>
  )
}
